import { useEffect, useState } from "react";
import { DynamicWorkflowToggle } from "@circle-workflows/components/DynamicWorkflowForm/DynamicWorkflowToggle";
import { useHeaderActions } from "@circle-workflows/components/ViewWorkflowModal/Header/HeaderActions/useHeaderActions";
import { useQueryClient } from "react-query";
import { WORKFLOW_STATUS } from "@circle-react/components/SettingsApp/Workflows/constants";
import type { Action } from "@circle-react/components/SettingsApp/Workflows/generated/WorkflowsApi.types";

interface Workflow {
  id: string;
  status: string;
  steps?: Action["action_type"][];
}

interface BotActiveToggleProps {
  agentId: number;
  workflow: Workflow;
}

export const BotActiveToggle = ({
  workflow,
  agentId,
}: BotActiveToggleProps) => {
  const { id: workflowId, status, steps } = workflow;
  const hasActions = Boolean(steps?.length);
  const isActive = status === WORKFLOW_STATUS.ACTIVE;
  const isDraft = status === WORKFLOW_STATUS.DRAFT;
  const queryClient = useQueryClient();

  const [isOptimisticallyActive, setIsOptimisticallyActive] =
    useState(isActive);

  useEffect(() => {
    setIsOptimisticallyActive(isActive);
  }, [isActive]);

  const { onChangeActiveStatus, isLoading } = useHeaderActions({
    workflowId,
    isActive,
  });

  const handleToggleChange = () => {
    setIsOptimisticallyActive(!isOptimisticallyActive);

    const queryKey = ["bot-workflows", agentId];
    const newStatus = isActive
      ? WORKFLOW_STATUS.INACTIVE
      : WORKFLOW_STATUS.ACTIVE;

    queryClient.setQueryData(queryKey, (oldData: unknown) => {
      if (!oldData || typeof oldData !== "object" || !("pages" in oldData)) {
        return oldData;
      }

      const data = oldData as {
        pages: Array<{ records?: Array<{ id: string; status: string }> }>;
      };

      return {
        ...data,
        pages: data.pages.map(page => ({
          ...page,
          records: page.records?.map(record =>
            record.id === workflowId
              ? { ...record, status: newStatus }
              : record,
          ),
        })),
      };
    });

    onChangeActiveStatus();
  };

  return (
    <DynamicWorkflowToggle
      isWorkflowActive={isOptimisticallyActive}
      isDisabled={!hasActions || isDraft}
      hasNoActions={!hasActions}
      isDraft={isDraft}
      isLoading={isLoading}
      onChange={handleToggleChange}
    />
  );
};
